import { Component, EventEmitter, inject, Input, Output, SimpleChanges } from '@angular/core';
import { ModalFormContainerComponent } from '../../ui/modal-form-container/modal-form-container.component';
import { SearchInputComponent } from '../../inputs/search-input/search-input.component';
import { LoadingSpinnerComponent } from '../../loading-spinner/loading-spinner.component';
import { PaginationComponent } from '../../inputs/pagination/pagination.component';
import { IContact, IPagination } from '../../../types/contactModule';
import { ContactsService } from '../../../services/contacts/contacts.service';
import { debounce } from 'lodash';
import { TContactSort } from '../../../pages/organization/contacts/contact-list/contact-list.component';
import { isSubset } from '../../../../utils/utils';
import { EmptyStateComponent } from '../../ui/empty-state/empty-state.component';
import { DataTableComponent } from '../../ui/data-table/data-table.component';

@Component({
  selector: 'assign-contacts-to-group',
  standalone: true,
  imports: [
    ModalFormContainerComponent,
    SearchInputComponent,
    LoadingSpinnerComponent,
    PaginationComponent,
    EmptyStateComponent,
    DataTableComponent,
  ],
  templateUrl: './assign-contacts-to-group.component.html',
  styleUrl: './assign-contacts-to-group.component.css',
})
export class AssignContactsToGroupComponent {
  constructor() {
    // Bind the debounced function
    this.debouncedSearch = debounce(this.searchContacts.bind(this), 1000);
  }

  @Input() title: string = 'Add Contacts';
  @Input() className: string = '';
  @Input() open: boolean = false;
  @Input() isLoading: boolean = false;
  @Output() close = new EventEmitter();
  // this will hold the already selected contacts
  @Input() selectedContacts: IContact[] = [];
  // this will emit the selected checked contacts to parent component
  @Output() assignContacts = new EventEmitter<IContact[]>();

  contactsService = inject(ContactsService);

  contacts: IContact[] = [];
  loading: boolean = false;

  showModal: boolean = false;

  searchTerm: string = '';
  sortCriteria: TContactSort = 'fullName,asc';

  contactsPagination: IPagination = {
    totalElements: 0,
    pageSize: 10,
    totalPages: 1,
    pageNumber: 0,
    offSet: 0,
  };

  // selection of contacts or checked contacts
  selectedCheckedContacts: IContact[] = []; // selected contacts
  selectContacts(contacts: IContact) {
    // Match by id, not object identity: contacts selected on another page or
    // passed in by the parent are different objects from the ones listed now.
    if (this.getContactIsSelected(contacts)) {
      this.selectedCheckedContacts = this.selectedCheckedContacts.filter(
        (el: IContact) => el.id !== contacts.id
      );
    } else {
      this.selectedCheckedContacts.push(contacts);
    }
  }

  /** "Add 3 contacts" — the footer button says what it will do. */
  get addLabel(): string {
    const count = this.selectedCheckedContacts.length;
    if (!count) return this.selectedContacts?.length ? 'Clear selection' : 'Add contacts';
    return count === 1 ? 'Add 1 contact' : `Add ${count} contacts`;
  }

  /** Nothing checked and nothing to clear: the button has nothing to do. */
  get addDisabled(): boolean {
    return !this.selectedCheckedContacts.length && !this.selectedContacts?.length;
  }

  selectAllContacts() {
    const contactIds = this.contacts.map((contact) => contact.id);
    contactIds.forEach((id) => {
      if (!this.selectedCheckedContacts.find((contact) => contact.id === id)) {
        const contactToAdd = this.contacts.find((contact) => contact.id === id);
        if (contactToAdd) {
          this.selectedCheckedContacts.push(contactToAdd);
        }
      }
    });
  }

  deSelectAllContacts() {
    const contactIds = this.contacts.map((contact) => contact.id);
    this.selectedCheckedContacts = this.selectedCheckedContacts.filter(
      (contact) => !contactIds.includes(contact.id)
    );
  }

  toggleSelectAllContacts() {
    if (isSubset(this.contacts, this.selectedCheckedContacts)) {
      this.deSelectAllContacts();
    } else {
      this.selectAllContacts();
    }
  }

  get getIsAllSelected() {
    return isSubset(this.contacts, this.selectedCheckedContacts);
  }

  getContactIsSelected(contact: IContact) {
    const checkedIds = this.selectedCheckedContacts.map((c) => c.id);
    return checkedIds.includes(contact.id);
  }

  get updatedParams() {
    return {
      ...this.params,
      page: this.contactsPagination.pageNumber,
      sort: this.sortCriteria,
    };
  }

  updateContactsPage(newPage: number) {
    this.contactsPagination.pageNumber = newPage;
    this.contactsService.fetchContacts(this.updatedParams);
  }
  goToNextPage() {
    this.contactsPagination.pageNumber += 1;
    // perform some api call
    this.contactsService.fetchContacts(this.updatedParams);
  }
  goToPreviousPage() {
    this.contactsPagination.pageNumber -= 1;
    // perform some api call
    this.contactsService.fetchContacts(this.updatedParams);
  }

  // params
  params: Record<string, any> = { size: 10, sort: `fullName,asc` };
  get reactiveParams() {
    return this.params;
  }

  // Debounced method
  debouncedSearch!: (...args: any[]) => void;

  updateSearchTerm(term: string) {
    this.searchTerm = term;
    if (term.length < 1) {
      delete this.params['fullName.contains'];
    } else {
      this.params['fullName.contains'] = term;
    }
    this.debouncedSearch();
  }

  searchContacts() {
    // Always send the full params: clearing the search used to send nothing,
    // which dropped the page size and sort.
    this.contactsService.fetchContacts(this.params);
  }

  closeModal() {
    this.close.emit();
  }

  handleAssignContactToGroup() {
    // console.log('selectedContacts :>> ', this.selectedCheckedContacts);
    // A copy, so later checks in this picker never change the parent's list in place.
    this.assignContacts.emit([...this.selectedCheckedContacts]);
    this.closeModal();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.showModal = this.open;
    if (this.isLoading !== null) {
      this.loading = this.isLoading;
    }
    // Mirror the parent's selection whenever it changes (including to empty,
    // after chips were removed) and each time the picker opens, so checks left
    // from a picker closed without adding do not linger.
    if (changes['selectedContacts'] || (changes['open'] && this.open)) {
      this.selectedCheckedContacts = [...(this.selectedContacts ?? [])];
    }
  }

  ngOnInit() {
    this.contactsService.fetchContacts(this.params);

    this.contactsService.getContacts.subscribe((contacts: IContact[]) => {
      this.contacts = contacts;
    });

    this.contactsService.getContactsPaginationData.subscribe(
      (data: IPagination) => {
        this.contactsPagination = data;
      }
    );
  }
}
